import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

/**
 * CollaborationService - Angular service for the Sales Team Collaboration module.
 *
 * Covers: threaded notes, replies, @mention resolution, resource sharing.
 * Backend base: /api/collaboration  (ecom-svc-admin)
 */
@Injectable({
  providedIn: 'root'
})
export class CollaborationService {

  private readonly base = environment.serverAPI4 + '/api/collaboration';

  constructor(private http: HttpClient) {}

  // ---------------------------------------------------------------------------
  // NOTES
  // ---------------------------------------------------------------------------

  /**
   * Save a root note or a reply.
   * Set `parentNoteId` on the note object to create a reply.
   */
  saveNote(note: any): Observable<any> {
    return this.http.post(`${this.base}/note`, note);
  }

  /**
   * Get paginated root-level notes for a resource.
   * @param resourceType  LEAD | DEAL | CONTACT | COMPANY
   * @param resourceId    The resource primary key
   */
  getRootNotes(resourceType: string, resourceId: number,
               page = 0, size = 20): Observable<any> {
    return this.http.get(
      `${this.base}/note/${resourceType}/${resourceId}?page=${page}&size=${size}`
    );
  }

  /** Get all replies for a parent note. */
  getReplies(noteId: number): Observable<any> {
    return this.http.get(`${this.base}/note/${noteId}/replies`);
  }

  /** Soft-delete a note. */
  deleteNote(noteId: number): Observable<any> {
    return this.http.delete(`${this.base}/note/${noteId}`);
  }

  // ---------------------------------------------------------------------------
  // RESOLVED MENTIONS
  // ---------------------------------------------------------------------------

  /**
   * After a note/task is saved, send the resolved mention list.
   * Each element: { userId, userName, userEmail, mentionedBy, mentionedByName }
   */
  saveMentions(contextType: string, contextId: number,
               resourceType: string, resourceId: number,
               mentions: any[]): Observable<any> {
    const params = `?resourceType=${resourceType}&resourceId=${resourceId}`;
    return this.http.post(
      `${this.base}/mention/${contextType}/${contextId}${params}`, mentions
    );
  }

  // ---------------------------------------------------------------------------
  // RESOURCE SHARING
  // ---------------------------------------------------------------------------

  /** Share a resource with a user. */
  shareResource(body: {
    resourceType: string;
    resourceId: number;
    userId: number;
    userName: string;
    sharedBy: number;
  }): Observable<any> {
    return this.http.post(`${this.base}/share`, body);
  }

  /** Revoke a user's access to a shared resource. */
  unshareResource(resourceType: string, resourceId: number, userId: number): Observable<any> {
    return this.http.delete(`${this.base}/share/${resourceType}/${resourceId}/${userId}`);
  }

  /** Get all shared user IDs for a resource (used for Solr reindex). */
  getSharedUsers(resourceType: string, resourceId: number): Observable<number[]> {
    return this.http.get<number[]>(`${this.base}/share/${resourceType}/${resourceId}/users`);
  }
}
